import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import CallRoundedIcon from '@mui/icons-material/CallRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined'
import EventRepeatRoundedIcon from '@mui/icons-material/EventRepeatRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import PhoneIcon from '@mui/icons-material/Phone'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useState } from 'react'
import { toast } from 'react-toastify'
import {
  BOOKING_PAYMENT_STATUSES,
  BOOKING_SESSION_STATUS,
  BOOKING_STATUSES,
  STATUS_SESSION_CANCELLED,
} from 'src/consumer/constants'

dayjs.extend(utc)

// Patient join links target the public consumer app (prod), mirroring SupportJoiningLinksDialog.
const PATIENT_JOIN_LINK_BASE = 'https://aumhum.com/v/'

/**
 * The URL a payment-pending booking should surface: the first non-paid, non-expired payment
 * link, falling back to the most recent link. Returns null when there's nothing to show.
 */
export function getSupportActivePaymentLinkUrl(bookedEvent) {
  const links = Array.isArray(bookedEvent?.paymentLinks) ? bookedEvent.paymentLinks : []
  if (links.length === 0) {
    return null
  }
  const active = links.find(
    (link) =>
      link?.paymentLink &&
      link?.paymentStatus !== 'PAID' &&
      (!link?.paymentLinkExpiredTime ||
        dayjs.utc(link.paymentLinkExpiredTime).isAfter(dayjs())),
  )
  if (active?.paymentLink) {
    return active.paymentLink
  }
  const newestWithUrl = [...links].reverse().find((link) => link?.paymentLink)
  return newestWithUrl?.paymentLink || null
}

/** Builds the patient joining link from the backend-enriched session slug (online bookings). */
export function getSupportPatientJoinLink(bookedEvent) {
  const slug = bookedEvent?.patientJoinSessionSlug
  return slug ? `${PATIENT_JOIN_LINK_BASE}${slug}` : null
}

/**
 * Visual system for the SUPPORT booked-events card ("support" bookingCardLayout only —
 * the mentor-portal card never imports these). One indigo accent, slate neutrals, calm
 * tinted status pills instead of the older mixed pastels/gradients.
 */
export const SUPPORT_BOOKING_UI = {
  accent: '#4f46e5',
  ink: '#0f172a',
  inkSecondary: '#334155',
  inkMuted: '#64748b',
  inkFaint: '#94a3b8',
  border: 'rgba(226, 232, 240, 0.9)',
  borderStrong: 'rgba(148, 163, 184, 0.4)',
  surface: '#ffffff',
  surfaceSoft: '#f8fafc',
  radiusCard: '12px',
  radiusControl: '8px',
}

// Each tone carries: bg/ink/border/dot (pills, tints) + strip (solid vertical-strip fill,
// contrasted by stripInk=white) + surface/surfaceBorder (the subtle card wash that follows the
// status, like the Control Center listing).
const STATUS_TONES = {
  blue: {
    bg: '#eff6ff', ink: '#1d4ed8', border: 'rgba(37, 99, 235, 0.24)', dot: '#3b82f6',
    strip: '#2563eb', stripInk: '#ffffff', surface: '#f6f9ff', surfaceBorder: 'rgba(37, 99, 235, 0.16)',
  },
  emerald: {
    bg: '#ecfdf5', ink: '#047857', border: 'rgba(5, 150, 105, 0.26)', dot: '#10b981',
    strip: '#059669', stripInk: '#ffffff', surface: '#f2fdf8', surfaceBorder: 'rgba(5, 150, 105, 0.18)',
  },
  amber: {
    bg: '#fffbeb', ink: '#b45309', border: 'rgba(217, 119, 6, 0.28)', dot: '#f59e0b',
    strip: '#d97706', stripInk: '#ffffff', surface: '#fffdf3', surfaceBorder: 'rgba(217, 119, 6, 0.2)',
  },
  red: {
    bg: '#fef2f2', ink: '#b91c1c', border: 'rgba(220, 38, 38, 0.26)', dot: '#ef4444',
    strip: '#dc2626', stripInk: '#ffffff', surface: '#fff5f5', surfaceBorder: 'rgba(220, 38, 38, 0.18)',
  },
  slate: {
    bg: '#f1f5f9', ink: '#475569', border: 'rgba(100, 116, 139, 0.26)', dot: '#94a3b8',
    strip: '#475569', stripInk: '#ffffff', surface: '#f8fafc', surfaceBorder: 'rgba(100, 116, 139, 0.2)',
  },
}

/**
 * Single primary status pill per card: the one state a support agent needs first.
 * Cancelled > payment pending > completed > in progress > booked.
 */
// A session that's marked in-progress but whose scheduled slot ended long ago is almost always a
// session the practitioner started and forgot to complete — not one genuinely still running. Past
// this buffer we relabel it so it stops masquerading as "In progress" for days.
const STALE_IN_PROGRESS_AFTER_END_MS = 2 * 60 * 60 * 1000 // 2h past scheduled end
const STALE_IN_PROGRESS_AFTER_START_MS = 3 * 60 * 60 * 1000 // 3h past start when no end time

function toMillis(value) {
  if (!value) {
    return null
  }
  const parsed = dayjs.utc(value)
  return parsed.isValid() ? parsed.valueOf() : null
}

/**
 * True when a booking is flagged in-progress but its slot ended long enough ago that it's really a
 * "started but never completed" session. `now` may be a dayjs/Date/millis; defaults to Date.now().
 */
export function isSupportSessionStaleInProgress(bookedEvent, now) {
  if (bookedEvent?.sessionStatus !== BOOKING_SESSION_STATUS.IN_PROGRESS) {
    return false
  }
  const nowMs =
    now == null
      ? Date.now()
      : typeof now === 'number'
        ? now
        : dayjs(now).valueOf()
  const endMs = toMillis(bookedEvent?.endTime)
  if (endMs != null) {
    return nowMs > endMs + STALE_IN_PROGRESS_AFTER_END_MS
  }
  const startMs = toMillis(bookedEvent?.startTime)
  if (startMs != null) {
    return nowMs > startMs + STALE_IN_PROGRESS_AFTER_START_MS
  }
  return false
}

export function getSupportBookingStatusMeta(bookedEvent, now) {
  if (bookedEvent?.status === STATUS_SESSION_CANCELLED) {
    return { label: 'Cancelled', stripLabel: 'CANCELLED', tone: 'red', ...STATUS_TONES.red }
  }
  if (
    bookedEvent?.status === BOOKING_STATUSES.BOOKING_INITIATED &&
    bookedEvent?.paymentStatus === BOOKING_PAYMENT_STATUSES.PENDING
  ) {
    return { label: 'Payment pending', stripLabel: 'UNPAID', tone: 'amber', ...STATUS_TONES.amber }
  }
  if (
    bookedEvent?.markedCompleted ||
    bookedEvent?.sessionStatus === BOOKING_SESSION_STATUS.MARKED_COMPLETED ||
    bookedEvent?.sessionStatus === BOOKING_SESSION_STATUS.FINISHED
  ) {
    return { label: 'Completed', stripLabel: 'COMPLETED', tone: 'slate', ...STATUS_TONES.slate }
  }
  if (bookedEvent?.sessionStatus === BOOKING_SESSION_STATUS.IN_PROGRESS) {
    if (isSupportSessionStaleInProgress(bookedEvent, now)) {
      return {
        label: 'Started, not completed',
        stripLabel: 'INCOMPLETE',
        tone: 'amber',
        staleInProgress: true,
        ...STATUS_TONES.amber,
      }
    }
    return { label: 'In progress', stripLabel: 'IN PROGRESS', tone: 'emerald', ...STATUS_TONES.emerald }
  }
  if (bookedEvent?.status === BOOKING_STATUSES.BOOKED) {
    return { label: 'Booked', stripLabel: 'BOOKED', tone: 'blue', ...STATUS_TONES.blue }
  }
  return { label: 'Initiated', stripLabel: 'INITIATED', tone: 'slate', ...STATUS_TONES.slate }
}

/**
 * The Control-Center-style vertical status strip that runs the full height of the card's left edge:
 * a solid status-colored column with the status label written bottom-to-top. Clickable when an
 * action is wired (e.g. Completed → participant logs) — otherwise a pure status indicator.
 */
export function SupportStatusStrip({ meta, onClick, title, width = 30 }) {
  const clickable = typeof onClick === 'function'
  const toneStyle = STATUS_TONES[meta?.tone] || STATUS_TONES.slate
  return (
    <Box
      onClick={
        clickable
          ? (event) => {
              event.stopPropagation()
              onClick(event)
            }
          : undefined
      }
      role={clickable ? 'button' : undefined}
      title={title || meta?.label}
      sx={{
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width,
        backgroundColor: meta?.strip || toneStyle.strip,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: clickable ? 'pointer' : 'default',
        userSelect: 'none',
        overflow: 'hidden',
        transition: 'filter 0.15s ease',
        '&:hover': clickable ? { filter: 'brightness(1.08)' } : undefined,
      }}
    >
      <Typography
        component="span"
        sx={{
          writingMode: 'vertical-rl',
          transform: 'rotate(180deg)',
          fontSize: 9,
          fontWeight: 800,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: meta?.stripInk || toneStyle.stripInk || '#ffffff',
          lineHeight: 1,
          whiteSpace: 'nowrap',
        }}
      >
        {meta?.stripLabel || meta?.label || ''}
      </Typography>
    </Box>
  )
}

/** Ink color for the "Starts in 45 mins" / "In progress for 20 mins" timing note. */
export function getSupportTimingNoteColor(sessionTimingMeta) {
  if (!sessionTimingMeta) {
    return SUPPORT_BOOKING_UI.inkMuted
  }
  if (sessionTimingMeta.attentionTone === 'in-progress') {
    return STATUS_TONES.emerald.ink
  }
  if (sessionTimingMeta.attentionTone === 'upcoming') {
    return STATUS_TONES.blue.ink
  }
  if (/never started|start time passed/i.test(sessionTimingMeta.displayLabel || '')) {
    return STATUS_TONES.amber.ink
  }
  return SUPPORT_BOOKING_UI.inkMuted
}

export function SupportStatusPill({ label, tone = 'slate', filled = true, pulse = false, onClick, title }) {
  const toneStyle = STATUS_TONES[tone] || STATUS_TONES.slate
  const clickable = typeof onClick === 'function'
  const pill = (
    <Box
      component={clickable ? 'button' : 'div'}
      type={clickable ? 'button' : undefined}
      onClick={
        clickable
          ? (event) => {
              event.stopPropagation()
              onClick(event)
            }
          : undefined
      }
      title={title}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.6,
        height: 24,
        px: 1.05,
        borderRadius: 999,
        backgroundColor: filled ? toneStyle.bg : 'transparent',
        border: `1px solid ${toneStyle.border}`,
        color: toneStyle.ink,
        fontSize: 12,
        fontWeight: 700,
        lineHeight: 1,
        whiteSpace: 'nowrap',
        flexShrink: 0,
        fontFamily: 'inherit',
        cursor: clickable ? 'pointer' : 'default',
        appearance: 'none',
        transition: 'filter 0.12s ease, box-shadow 0.12s ease',
        ...(clickable
          ? {
              '&:hover': {
                filter: 'brightness(0.97)',
                boxShadow: `0 0 0 3px ${toneStyle.bg}`,
              },
            }
          : {}),
      }}
    >
      <Box
        sx={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          backgroundColor: toneStyle.dot,
          flexShrink: 0,
          ...(pulse
            ? {
                animation: 'support-pill-pulse 1.6s ease-in-out infinite',
                '@keyframes support-pill-pulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.35 },
                },
              }
            : {}),
        }}
      />
      {label}
    </Box>
  )
  return pill
}

/**
 * Compact date/time block on the card's left edge (sm+) or an inline row (xs).
 * Replaces the old full-height gradient rail.
 */
export function SupportTimeBlock({
  startLabel,
  endLabel,
  dateLine,
  isToday = false,
  meetingModeLabel,
  MeetingModeIcon,
  onClick,
}) {
  const clickable = typeof onClick === 'function'
  return (
    <Box
      component={clickable ? 'button' : 'div'}
      type={clickable ? 'button' : undefined}
      onClick={
        clickable
          ? (event) => {
              event.stopPropagation()
              onClick(event)
            }
          : undefined
      }
      title={clickable ? 'Quick actions for this booking' : undefined}
      sx={{
        display: 'flex',
        flexDirection: { xs: 'row', sm: 'column' },
        alignItems: { xs: 'center', sm: 'flex-start' },
        flexWrap: 'wrap',
        gap: { xs: 1, sm: 0.3 },
        pr: { sm: 1.5 },
        py: clickable ? 0.4 : 0,
        pl: clickable ? 0.4 : 0,
        ml: clickable ? -0.4 : 0,
        borderRight: { sm: `1px solid ${SUPPORT_BOOKING_UI.border}` },
        borderRadius: clickable ? '8px' : 0,
        minWidth: 0,
        alignSelf: 'stretch',
        fontFamily: 'inherit',
        textAlign: 'left',
        appearance: 'none',
        border: clickable ? '1px solid transparent' : undefined,
        backgroundColor: 'transparent',
        cursor: clickable ? 'pointer' : 'default',
        transition: 'background-color 0.12s ease, border-color 0.12s ease',
        ...(clickable
          ? {
              '&:hover': {
                backgroundColor: '#f8fafc',
                borderColor: 'rgba(79, 70, 229, 0.28)',
              },
            }
          : {}),
      }}
    >
      <Typography
        sx={{
          fontSize: 15,
          fontWeight: 800,
          color: SUPPORT_BOOKING_UI.ink,
          lineHeight: 1.15,
          whiteSpace: 'nowrap',
        }}
      >
        {startLabel}
      </Typography>
      {endLabel ? (
        <Typography
          sx={{
            fontSize: 11.5,
            fontWeight: 600,
            color: SUPPORT_BOOKING_UI.inkFaint,
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
          }}
        >
          – {endLabel}
        </Typography>
      ) : null}
      <Typography
        sx={{
          mt: { sm: 0.35 },
          fontSize: 11.5,
          fontWeight: isToday ? 800 : 650,
          color: isToday ? SUPPORT_BOOKING_UI.accent : SUPPORT_BOOKING_UI.inkMuted,
          lineHeight: 1.25,
          whiteSpace: 'nowrap',
        }}
      >
        {dateLine}
      </Typography>
      {MeetingModeIcon ? (
        <Stack direction="row" spacing={0.45} alignItems="center" sx={{ mt: { sm: 0.35 } }}>
          <MeetingModeIcon sx={{ fontSize: 14, color: SUPPORT_BOOKING_UI.inkFaint }} />
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 650,
              color: SUPPORT_BOOKING_UI.inkMuted,
              lineHeight: 1,
              whiteSpace: 'nowrap',
            }}
          >
            {meetingModeLabel}
          </Typography>
        </Stack>
      ) : null}
    </Box>
  )
}

function formatHistoryInstant(value) {
  if (!value) {
    return null
  }
  return dayjs.utc(value).local().format('DD MMM YYYY, h:mm A')
}

/**
 * The "Rescheduled" status pill that opens a drawer of the booking's reschedule history
 * (from modificationHistory) on click. Self-contained — owns its own dialog state.
 */
export function SupportRescheduleHistoryChip({ bookedEvent }) {
  const [open, setOpen] = useState(false)
  const rescheduleEntries = (bookedEvent?.modificationHistory || []).filter(
    (entry) => entry?.event === 'booking-rescheduled',
  )
  const currentStartLabel = bookedEvent?.startTime
    ? dayjs.utc(bookedEvent.startTime).local().format('DD MMM YYYY, h:mm A')
    : null

  return (
    <>
      <SupportStatusPill
        label="Rescheduled"
        tone="blue"
        filled={false}
        onClick={() => setOpen(true)}
        title="View reschedule history"
      />
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '14px' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <EventRepeatRoundedIcon sx={{ color: '#4f46e5' }} />
            <Typography sx={{ fontWeight: 800, fontSize: 16, color: '#0f172a' }}>
              Reschedule history
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {rescheduleEntries.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: '#64748b' }}>
              No reschedule records are available for this booking.
            </Typography>
          ) : (
            <Stack spacing={1.25} sx={{ mt: 0.5 }}>
              {currentStartLabel ? (
                <Box
                  sx={{
                    p: 1.1,
                    borderRadius: '10px',
                    border: '1px solid rgba(5, 150, 105, 0.26)',
                    backgroundColor: 'rgba(5, 150, 105, 0.06)',
                  }}
                >
                  <Typography sx={{ fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', color: '#94a3b8' }}>
                    Current slot
                  </Typography>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 750, color: '#047857' }}>
                    {currentStartLabel}
                  </Typography>
                </Box>
              ) : null}
              {[...rescheduleEntries].reverse().map((entry, index) => (
                <Box
                  key={`${entry?.modifiedAt || index}`}
                  sx={{
                    p: 1.1,
                    borderRadius: '10px',
                    border: '1px solid rgba(226, 232, 240, 0.9)',
                  }}
                >
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>
                    Moved from{' '}
                    {formatHistoryInstant(entry?.startTimeBeforeModification) || 'an earlier slot'}
                  </Typography>
                  {entry?.modifiedAt ? (
                    <Typography sx={{ fontSize: 12, color: '#64748b', mt: 0.15 }}>
                      {formatHistoryInstant(entry.modifiedAt)}
                    </Typography>
                  ) : null}
                  {entry?.modificationReason ? (
                    <Typography sx={{ fontSize: 12.5, color: '#475569', mt: 0.35 }}>
                      Reason: {entry.modificationReason}
                    </Typography>
                  ) : null}
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} color="inherit">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

/**
 * The patient phone chip on the support card. Clicking it opens a small menu with Call and
 * WhatsApp — the same two channels the contact strip offers, but reachable straight from the
 * number. Self-contained: uses tel:/wa.me so it needs no external handlers.
 */
export function SupportPhoneActions({ phoneLabel, rawPhone, onOpen }) {
  const [anchorEl, setAnchorEl] = useState(null)
  if (!phoneLabel) {
    return null
  }
  const digits = String(rawPhone || phoneLabel).replace(/[^\d+]/g, '')
  const waDigits = digits.replace(/^\+/, '')
  // When the page supplies a handler, defer to a page-level contact dialog — a per-card anchored
  // Menu gets torn down every time the listing re-renders (the 1-min "starts in N" tick), which
  // silently closed the call options mid-interaction. The local Menu stays as a standalone fallback.
  const delegatesToPage = typeof onOpen === 'function'

  return (
    <>
      <Stack
        component="button"
        type="button"
        direction="row"
        spacing={0.4}
        alignItems="center"
        onClick={(event) => {
          event.stopPropagation()
          if (delegatesToPage) {
            onOpen()
            return
          }
          setAnchorEl(event.currentTarget)
        }}
        sx={{
          appearance: 'none',
          border: 0,
          background: 'transparent',
          p: 0,
          m: 0,
          cursor: 'pointer',
          fontFamily: 'inherit',
          color: '#4338ca',
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        <PhoneIcon sx={{ fontSize: 13, color: '#818cf8' }} />
        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: 'inherit' }}>{phoneLabel}</Typography>
      </Stack>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        onClick={(event) => event.stopPropagation()}
        slotProps={{ paper: { sx: { borderRadius: '10px', minWidth: 180 } } }}
      >
        <MenuItem
          onClick={() => {
            setAnchorEl(null)
            window.open(`tel:${digits}`, '_self')
          }}
        >
          <ListItemIcon>
            <CallRoundedIcon fontSize="small" sx={{ color: '#2563eb' }} />
          </ListItemIcon>
          <ListItemText primary="Call" secondary={phoneLabel} />
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchorEl(null)
            window.open(`https://wa.me/${waDigits}`, '_blank', 'noopener')
          }}
        >
          <ListItemIcon>
            <WhatsAppIcon fontSize="small" sx={{ color: '#128c7e' }} />
          </ListItemIcon>
          <ListItemText primary="WhatsApp" secondary={phoneLabel} />
        </MenuItem>
      </Menu>
    </>
  )
}

/**
 * A compact inline link row for the support card (payment link, patient joining link) with a
 * copy button. `copyPrefix` is prepended to the copied text only (e.g. "Joining link: ") — the
 * displayed/opened URL stays clean.
 */
export function SupportInlineLinkRow({ label, url, icon, tone = 'slate', copyPrefix = '' }) {
  const [copied, setCopied] = useState(false)
  if (!url) {
    return null
  }
  const toneStyle = STATUS_TONES[tone] || STATUS_TONES.slate

  async function handleCopy(event) {
    event.stopPropagation()
    try {
      await navigator.clipboard.writeText(`${copyPrefix}${url}`)
      setCopied(true)
      toast.success(`${label} copied`)
      setTimeout(() => setCopied(false), 1600)
    } catch (error) {
      toast.error('Could not copy the link')
    }
  }

  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      sx={{
        mt: 0.5,
        px: 0.9,
        py: 0.5,
        borderRadius: '8px',
        border: `1px solid ${toneStyle.border}`,
        backgroundColor: toneStyle.bg,
        minWidth: 0,
        maxWidth: '100%',
      }}
    >
      {icon ? <Box sx={{ display: 'flex', color: toneStyle.ink }}>{icon}</Box> : null}
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 800,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: toneStyle.ink,
          flexShrink: 0,
        }}
      >
        {label}
      </Typography>
      <Typography
        component="a"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(event) => event.stopPropagation()}
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize: 12,
          fontWeight: 600,
          color: toneStyle.ink,
          textDecoration: 'none',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        {url}
      </Typography>
      <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
        <IconButton size="small" onClick={handleCopy} sx={{ p: 0.35, color: toneStyle.ink }}>
          {copied ? (
            <CheckRoundedIcon sx={{ fontSize: 15 }} />
          ) : (
            <ContentCopyRoundedIcon sx={{ fontSize: 14 }} />
          )}
        </IconButton>
      </Tooltip>
      <Tooltip title="Open in new tab">
        <IconButton
          size="small"
          onClick={(event) => {
            event.stopPropagation()
            window.open(url, '_blank', 'noopener')
          }}
          sx={{ p: 0.35, color: toneStyle.ink }}
        >
          <OpenInNewRoundedIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  )
}

export function SupportBookingsEmptyState({ searchActive = false }) {
  return (
    <Stack alignItems="center" spacing={1.25} sx={{ py: 7, px: 2, textAlign: 'center' }}>
      <Box
        sx={{
          width: 52,
          height: 52,
          borderRadius: '16px',
          display: 'grid',
          placeItems: 'center',
          backgroundColor: 'rgba(79, 70, 229, 0.07)',
          color: SUPPORT_BOOKING_UI.accent,
        }}
      >
        {searchActive ? (
          <EventBusyOutlinedIcon sx={{ fontSize: 26 }} />
        ) : (
          <CalendarMonthOutlinedIcon sx={{ fontSize: 26 }} />
        )}
      </Box>
      <Typography sx={{ fontSize: 15, fontWeight: 750, color: SUPPORT_BOOKING_UI.ink }}>
        No bookings found
      </Typography>
      <Typography sx={{ fontSize: 13, color: SUPPORT_BOOKING_UI.inkMuted, maxWidth: 380 }}>
        Try widening the date range, switching tabs, or clearing a filter — cancelled and
        payment-pending bookings may also be hidden by the current toggles.
      </Typography>
    </Stack>
  )
}

export function SupportBookingCardSkeleton() {
  return (
    <Box
      sx={{
        borderRadius: SUPPORT_BOOKING_UI.radiusCard,
        border: `1px solid ${SUPPORT_BOOKING_UI.border}`,
        backgroundColor: SUPPORT_BOOKING_UI.surface,
        p: { xs: 1.5, sm: 1.75 },
        mb: 1.25,
      }}
    >
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Stack spacing={0.5} sx={{ width: 86, flexShrink: 0 }}>
          <Skeleton variant="text" width={64} height={22} />
          <Skeleton variant="text" width={72} height={16} />
          <Skeleton variant="text" width={54} height={14} />
        </Stack>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Skeleton variant="text" width="42%" height={24} />
          <Skeleton variant="text" width="64%" height={18} />
          <Skeleton variant="text" width="30%" height={16} />
        </Box>
        <Skeleton variant="rounded" width={92} height={24} sx={{ borderRadius: 999 }} />
      </Stack>
      <Stack direction="row" spacing={1} sx={{ mt: 1.5, pt: 1.25, borderTop: `1px solid ${SUPPORT_BOOKING_UI.border}` }}>
        <Skeleton variant="rounded" width={76} height={30} sx={{ borderRadius: '8px' }} />
        <Skeleton variant="rounded" width={92} height={30} sx={{ borderRadius: '8px' }} />
        <Skeleton variant="rounded" width={104} height={30} sx={{ borderRadius: '8px' }} />
        <Box sx={{ flex: 1 }} />
        <Skeleton variant="rounded" width={96} height={30} sx={{ borderRadius: '8px' }} />
        <Skeleton variant="rounded" width={72} height={30} sx={{ borderRadius: '8px' }} />
      </Stack>
    </Box>
  )
}
